import { hmac } from '@noble/hashes/hmac'
import { sha256 } from '@noble/hashes/sha256'
import { utf8ToBytes } from '@noble/hashes/utils'

/**
 * Parameters of the deterministic domain sequence. These are public constants
 * baked into both the client PWA and the API server — the secrecy of `seed` is
 * deliberately NOT the security boundary (the ed25519 beacon signature is).
 * The point of the formula is an infinite sequence both sides derive on their
 * own, so there is no finite fallback list to hard-code, run out of, or leak.
 */
export interface DgaConfig {
  /** Length of the generated domain label (before the dot). */
  labelLength: number
  /** Public domain-derivation seed. */
  seed: string
  /** Allowed TLDs, drawn from deterministically. Never include `vn`. */
  tlds: string[]
  /** Algorithm/contract version. Bumping it forks the whole sequence. */
  version: number
}

const LETTERS = 'abcdefghijklmnopqrstuvwxyz'
const ALNUM = 'abcdefghijklmnopqrstuvwxyz0123456789'

/**
 * Derive the domain at a given position in the sequence. Pure and deterministic:
 * identical inputs yield an identical, registerable domain (`<letter-led label>.<tld>`).
 */
export const deriveDomain = (index: number, config: DgaConfig): string => {
  const digest = hmac(sha256, utf8ToBytes(config.seed), utf8ToBytes(`${config.version}:${index}`))

  // First char is always a letter so the label is a valid, non-all-numeric DNS label.
  let label = LETTERS[digest[0] % LETTERS.length]
  for (let i = 1; i < config.labelLength; i++) {
    label += ALNUM[digest[i % digest.length] % ALNUM.length]
  }

  const tld = config.tlds[digest[digest.length - 1] % config.tlds.length]
  return `${label}.${tld}`
}

/** Derive `count` consecutive domains starting at `startIndex`. */
export const deriveSequence = (startIndex: number, count: number, config: DgaConfig): string[] =>
  Array.from({ length: count }, (_, i) => deriveDomain(startIndex + i, config))
